import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation, ElementRef, isDevMode, OnDestroy, NgZone, AfterViewInit } from '@angular/core';
import { debounceTime, map } from 'rxjs/operators';
import { UserAccountModel, StarbaseComponentMessage, MONTHS, ContactModel, IEditCheck } from 'starbase-launcher';
import { UserDataService } from '../services/user-data.service';
import { StarbaseComponentMessageService } from '../services/starbase-component-message.service';
import { PasswordScoreEnum } from 'account-management';
import { AppGlobalsService } from '../app-globals.service';
import { controlsEqualValidator, emailAvailableValidator, passwordScoreValidator, StarbaseWizardStep } from 'starbase-wizard';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { LU_SubscriptionPriceModel_ext } from '../models/lu_subscriptionprice';
import { StarbaseModalButton, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalTypes } from 'starbase-modal-dialog';
import { LuSubscriptionPriceService } from '../services/lu-subscriptionprice.service';
import { StarbaseLoginService } from 'starbase-login';
import { environment } from '../../environments/environment';
import { PlanChoicesComponent } from '../plan-choices/plan-choices.component';
import { Observable, of } from 'rxjs';
import { BreakpointService, BreakpointState} from '../breakpoint.service';
import { StarbaseNotificationSentiment, StarbaseNotificationService } from 'starbase-notification';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.css'],
  encapsulation: ViewEncapsulation.Emulated
})
export class SignUpComponent implements OnInit, IEditCheck, AfterViewInit, OnDestroy {
  @ViewChild('suStep0', { static: true }) step0Template: TemplateRef<any>;
  @ViewChild('suStep1', { static: true }) step1Template: TemplateRef<any>;
  @ViewChild('suStep2', { static: true }) step2Template: TemplateRef<any>;
  // @ViewChild('createAcctForm', { static: true }) createAccountForm: Form;

  currentBreakpoint: BreakpointState;
  isHandset = false;

  signupUserMode = false;
  appMinimumPwdScore: PasswordScoreEnum;
  appMinimumPwdLength: number;
  currentPwdScoreObj: { currentScore: PasswordScoreEnum; minimumPwdScore: PasswordScoreEnum };
  passwordRulesText: string;
  chosenPlan: LU_SubscriptionPriceModel_ext;
  waiting = false;

  suSteps: StarbaseWizardStep<UserAccountModel>[] = [];

  months = MONTHS;

  outputObject: UserAccountModel = new UserAccountModel(0, '', '', '', false);
  currentStep = 0;
  showFinalBtn = false;

  prePlanned = 0;

  emailFrom = environment.testMode ? environment.testSupportEmailAddress : environment.supportEmailAddress;

  captchaVerified = false;
  siteKey = environment.recaptcha.siteKey;

  private _subscription;

  constructor(
    private modalSvc: StarbaseModalDialogService,
    private userSvc: UserDataService,
    private loginSvc: StarbaseLoginService,
    private subscriptionPlanSvc: LuSubscriptionPriceService,
    private appGlobalsSvc: AppGlobalsService,
    private sbCmpMsgSvc: StarbaseComponentMessageService,
    private notiSvc: StarbaseNotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private breakpointService: BreakpointService,
    private elements: ElementRef) {
      this.appMinimumPwdScore = this.appGlobalsSvc.minimumPwdScore;
    this.appMinimumPwdLength = this.appGlobalsSvc.minimumPwdLength;
    this.passwordRulesText = `Password must contain: \n
    • At least ${this.appMinimumPwdLength} characters\n
    • 1 capital letter\n
    • 1 lower-case letter\n
    • 1 number\n
    • Approved Symbols:\n
      !@$^*(){}[]|\/:;"'<>,.?\n`;
  }

  get devOrTestingMode(): boolean {
    return isDevMode() || environment.testMode;
  }

  ngOnInit() {
    this.currentPwdScoreObj = {
      currentScore: PasswordScoreEnum.Blank,
      minimumPwdScore: this.appMinimumPwdScore
    };
    this.loadSubPlanPrices();
    //web or handset subscription through breakpoint service.
    this._subscription = this.breakpointService.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
    );

    if (this.loginSvc.isLoggedIn()) {
      this.sbCmpMsgSvc.handleStarbaseComponentMsg(
        new StarbaseComponentMessage(
        'Cannot sign up when already logged in. Please log out before using the sign-up system.'
      ));
      this.router.navigateByUrl('/gateway');
    }
    this.outputObject.contact = new ContactModel();
    // this.outputObject['phoneNumber'] = '';
    this.suSteps = this.buildStepForms();
    this.signupUserMode = true;

    this.route.queryParams.subscribe(params => {
        this.prePlanned = +params.plan;
    });

    this.gotoFirstStep();
    window.scrollTo({ top: 0, behavior: 'smooth'});
  }

  ngAfterViewInit(): void {
      this.setFocus();
  }

  ngOnDestroy(): void {
    if(this._subscription){
      this._subscription.unsubscribe();
    }
  }

  resolvedRecaptcha(event) {
    if (event) {
      this.userSvc.verifyRecaptcha(event).subscribe(result => {
        if (result) {
          this.captchaVerified = true;
        } else {
          this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
            'Failed Verification',
            'Please refresh and try again.', undefined, undefined,
            StarbaseNotificationSentiment.Error
          ));
        }
      });
    }
  }

  unsavedChangesCheck(): Observable<boolean> {
    if (this.showFinalBtn === true) {
      return of(true);
    } else if (!this.chosenPlan) {
      return this.confirmCancelSignup();
    } else {
      return of(false);
    }
  }

  redirectToLogin(emailID?: string) {
    if (emailID) {
      this.router.navigate(['/login'], {
        queryParams: {email: emailID},
        state: {message: 'Account already exists, please login instead.'}
      })
    } else {
      this.router.navigateByUrl('/login');
    }
  }

  cancelSignup(): void {
    this.router.navigateByUrl('/');
  }

  openPaymentForm(plan: LU_SubscriptionPriceModel_ext): void {
    if (this.suSteps[1].stepFormGroup.invalid) {
      console.warn('Attempted to proceed with invalid form.');
      return;
    }
    if (plan.name === 'Student' && !this.outputObject.email.includes('.edu')) {
      console.warn('Attempted to choose student plan without an @.edu account.');
      this.chosenPlan = undefined;
      this.modalSvc.postModal(
        new StarbaseModalOptions(
          'Invalid plan choice',
          'You must use a student ".edu" email address in order to select the Student plan.',
          [],
          StarbaseModalTypes.Warning
        ));
      this.gotoPlanSelection();
      return;
    }
    if (plan.name === 'Free') {
      this.finallyDone();
    }
    this.chosenPlan = plan;
    this.currentStep = 2;
    this.suSteps[1].interStepFunc(undefined);
  }
  gotoFirstStep(): void {
    this.currentStep = 0;
    this.suSteps[0].stepFormGroup.enable();
    // this.setFocus();
  }
  gotoPlanSelection(): void {
    if (this.suSteps[0].stepFormGroup.invalid) {
      console.warn('Attempted to proceed with invalid form.');
      // console.debug(this.suSteps[0].stepFormGroup);
      return;
    }
    if (this.chosenPlan) { // if pre-planned from Bark URL
      this.openPaymentForm(this.chosenPlan);
    } else {
      this.currentStep = 1;
    }
  }
  gotoLoginScreen(): void {
    this.router.navigateByUrl('/login');
  }

  checkEmailTaken = (email: string) => this.userSvc.getRequisiteData<boolean>(this.userSvc.UsernameAllowedUri + email).pipe(
    debounceTime(1000)
  );
  pwdScore = (pwd: string) => this.userSvc.getRequisiteData(this.userSvc.PasswordScoreUri + pwd).pipe(
    debounceTime(1000),
    map(s => s as PasswordScoreEnum)
  );

  public setFocus(): void {
    setTimeout(() => {
      const inputFocusing = this.elements.nativeElement.querySelector('input');
      // inputFocusing.onload( _ => inputFocusing.focus());
      if (inputFocusing) {
        inputFocusing.focus();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        console.warn('Sign-up interface could not find a suitable element for setting focus.');
      }
    }, 400);
  }

  signUpNewUser() {
    if (this.waiting) {
      return;
    }
    if (this.suSteps[0].stepFormGroup.invalid) {
      console.warn('Attempted to proceed with invalid form.');
      return;
    }
    this.waiting = true;
    this.suSteps[0].stepFormGroup.disable();
    this.outputObject = this.createUserFinalize(this.outputObject, this.suSteps[0].stepFormGroup);
    // console.debug(this.outputObject);
    this.userSvc.postData(this.userSvc.SignupNewAccountUri, this.outputObject).subscribe(
      (u: UserAccountModel) => {
        this.outputObject.uid = u.uid; this.outputObject.contactUID = u.contactUID; this.outputObject.contact.contactUID = u.contactUID;
        const message = StarbaseComponentMessage.successMessageForUser('Registration succeeded', 'Your account has been created!');
        this.sbCmpMsgSvc.handleStarbaseComponentMsg(message);
        this.waiting = false;
        this.gotoPlanSelection();
      },
      () => {
        const message = StarbaseComponentMessage.errorMessageForUser(
          'Registration failed', 'Sorry, your account could not be created at this time. Please try again or contact support.');
        this.sbCmpMsgSvc.handleStarbaseComponentMsg(message);
        this.waiting = false;
        this.suSteps[0].stepFormGroup.enable();
      }
    );
  }

  finallyDone(): void {
    this.showFinalBtn = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadSubPlanPrices(): void {
    this.subscriptionPlanSvc.getAllData(this.subscriptionPlanSvc.getActiveSubscriptionsUri).subscribe(
      (data) => {
        this.appGlobalsSvc.allSubscriptionPlans = [
          data.find(p => p.name === 'Monthly'),
          data.find(p => p.name === 'Annual'),
          data.find(p => p.name === 'Student'),
          data.find(p => p.name === 'Daily (new)')
        ];
        if (this.prePlanned !== 0) { // if coming from Bark pages...
          switch (this.prePlanned) {
            case 1:
              this.chosenPlan = PlanChoicesComponent.FreePlan(); // free plan
              break;
            case 2:
              this.chosenPlan = this.appGlobalsSvc.allSubscriptionPlans[0]; // monthly plan
              break;
            case 3:
              this.chosenPlan = this.appGlobalsSvc.allSubscriptionPlans[1]; // annual plan
              break;
            case 4:
              this.chosenPlan = this.appGlobalsSvc.allSubscriptionPlans[2]; // student plan
              break;
            default:
              this.chosenPlan = undefined;
              break;
          }
        }
      }
    );
  }

  private buildStepForms(): StarbaseWizardStep<UserAccountModel>[] {
    const step0 = new StarbaseWizardStep<UserAccountModel>('STEP 1: Create an Account', this.step0Template);
    const step1 = new StarbaseWizardStep<UserAccountModel>('STEP 2: Select a plan', this.step1Template);
    const step2 = new StarbaseWizardStep<UserAccountModel>('STEP 3: Enter your payment info', this.step2Template);

    step0.stepFormGroup = this.fb.group({
      firstName: new FormControl('',
      [
        Validators.required,
        Validators.minLength(2)
      ]),
      lastName: new FormControl('',
      [
          Validators.required,
          Validators.minLength(2)
      ]),
      // phoneNumber: new FormControl('',
      //   {
      //     validators: [
      //       Validators.maxLength(14)
      //     ]}
      // ),
      // birthMonth: new FormControl(0,
      //   [
      //     Validators.min(0),
      //     Validators.max(12)
      //   ]),
      // birthDate: new FormControl(0,
      //   [
      //     Validators.min(0),
      //     Validators.max(31)
      //   ]),
      // birthYear: new FormControl(0,
      //   [
      //     Validators.min(0),
      //     Validators.max(new Date().getFullYear())
      //   ])
      userName: new FormControl('',
      [
          Validators.maxLength(50)
      ]),
      email: new FormControl('',
      {
        validators: [
          Validators.required,
          Validators.email,
          Validators.minLength(5),
          Validators.maxLength(320)
        ],
        asyncValidators: [
          emailAvailableValidator(this.checkEmailTaken)
        ],
        updateOn: 'blur'
      }),
      //email2: new FormControl('',
      //{
      //  validators: [
      //    Validators.required,
      //    Validators.email,
      //    Validators.minLength(5),
      //    Validators.maxLength(320)
      //  ]
      //}),
      password: new FormControl('',
      {
        validators: [
          Validators.required,
          //Validators.pattern(/[^\s]*/g),
          Validators.minLength(this.appMinimumPwdLength),
        ],
        asyncValidators:
          passwordScoreValidator(this.pwdScore, this.currentPwdScoreObj)
        ,
        updateOn: 'change'
      })//,
      //confirmPassword: new FormControl('', [
      //  Validators.required,
      //  Validators.minLength(this.appMinimumPwdLength)])
    }//,
    //{
    //  validators: [ // form-wide validators
    //    controlsEqualValidator('password', 'confirmPassword'),
    //    controlsEqualValidator('email', 'email2')
    //  ]
    //}
    );

    step1.stepFormGroup = this.fb.group({});

    step1.interStepFunc = _ => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return of();
    };

    step2.stepFormGroup = this.fb.group({});

    return [
      step0,
      step1,
      step2
    ];
  }

  private confirmCancelSignup(): Observable<boolean> {
    if (this.waiting) {
      return of(false);
    } // disallow nav until done loading
    const proceed = () => {
      this.signupUserMode = false;
      this.chosenPlan = undefined;
      this.modalSvc.clearSession();
    };
    if (this.suSteps[0].stepFormGroup.dirty && !this.showFinalBtn) {
      return new Observable<boolean>(o => {
        const m = new StarbaseModalOptions(
          'Cancel Signup?',
          'Are you sure you want to cancel signing up for Research Arsenal?',
          [
            new StarbaseModalButton('Yes',
              () => {
                proceed(); o.next(true);
              }),
            new StarbaseModalButton('No',
              () => {
                o.next(false);
                this.modalSvc.closeModal('User chose to stay');
              })
          ],
          StarbaseModalTypes.Warning
        );
        m.dismissable = false;
        this.modalSvc.postModal(m);
      });
    } else {
      proceed();
      return of(true);
    }
  }

  private createUserFinalize(newUserAccount: UserAccountModel, form: FormGroup): UserAccountModel {
    newUserAccount.active = true;
    newUserAccount.firstName = form.value['firstName'];
    newUserAccount.lastName = form.value['lastName'];
    newUserAccount.password = form.value['password'];
    newUserAccount.email = form.value['email'];
    /* ---- */
    newUserAccount.contact.firstName = newUserAccount.firstName;
    newUserAccount.contact.lastName = newUserAccount.lastName;
    newUserAccount.contact.organization = '';
    newUserAccount.userName = newUserAccount.firstName || newUserAccount.lastName;
    newUserAccount['confirmPassword'] = undefined;
    // newUserAccount.contact.birthDate = newUserAccount['birthDate'];
    // newUserAccount.contact.birthMonth = +newUserAccount['birthMonth'];
    // newUserAccount.contact.birthYear = newUserAccount['birthYear'];
    newUserAccount.user = newUserAccount.email;
    newUserAccount.contact.commTypesPhone = [];
    // newUserAccount.contact.commTypesPhone.push(new ContactPhone().deserialize({
    //   uid: 0,
    //   phoneNumber: newUserAccount['phoneNumber'],
    //   contactUID: 0,
    //   preferred: true
    // }));
    return newUserAccount;
  }


}
